import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Facebook, Instagram, MessageCircle, CheckCircle, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        // Simulate form submission
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        toast.success('Съобщението е изпратено! Ще се свържем с вас скоро.');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setLoading(false);
    };

    const workingHours = [
        { day: 'Понеделник', hours: '08:30 - 20:00', isOpen: true },
        { day: 'Вторник', hours: '08:30 - 20:00', isOpen: true },
        { day: 'Сряда', hours: '08:30 - 20:00', isOpen: true },
        { day: 'Четвъртък', hours: '08:30 - 20:00', isOpen: true },
        { day: 'Петък', hours: '08:30 - 20:00', isOpen: true },
        { day: 'Събота', hours: '09:00 - 20:00', isOpen: true },
        { day: 'Неделя', hours: '09:00 - 20:00', isOpen: true },
    ];

    const socialLinks = [
        { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61574417498282', color: 'from-blue-500 to-blue-600' },
        { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/vkusyt_na_turciq/', color: 'from-pink-500 to-purple-600' },
        { name: 'TikTok', icon: MessageCircle, href: 'https://www.tiktok.com/@vkusatnaturciq', color: 'from-slate-800 to-slate-900' },
    ];

    // Get current day to highlight
    const today = new Date().getDay();
    const dayIndex = today === 0 ? 6 : today - 1; // Convert to Mon=0, Sun=6

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
            {/* Hero */}
            <section className="relative py-24 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-white overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1200')] bg-cover bg-center opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-r from-amber-600/90 via-orange-500/90 to-amber-600/90" />
                <div className="container-main relative z-10">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
                            <Phone className="w-4 h-4" /> Винаги на линия
                        </span>
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Контакти</h1>
                        <p className="text-white/90 text-lg md:text-xl leading-relaxed">
                            Имате въпроси? Свържете се с нас и ще ви помогнем.
                        </p>
                    </div>
                </div>
            </section>

            {/* Quick Contact CTA */}
            <section className="py-8 -mt-12 relative z-10">
                <div className="container-main">
                    <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-amber-100">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center shadow-lg animate-pulse">
                                    <PhoneCall className="h-8 w-8 text-white" />
                                </div>
                                <div>
                                    <p className="text-slate-500 text-sm">Обадете се сега</p>
                                    <a href="tel:0899972727" className="text-2xl md:text-3xl font-bold text-slate-800 hover:text-amber-600 transition-colors">
                                        0899 97 2727
                                    </a>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                {socialLinks.map((social, i) => (
                                    <a
                                        key={i}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${social.color} flex items-center justify-center shadow-lg hover:scale-110 transition-transform`}
                                    >
                                        <social.icon className="h-6 w-6 text-white" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16 bg-white">
                <div className="container-main">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Info */}
                        <div>
                            <h2 className="font-serif text-3xl font-bold mb-8 text-slate-800">Свържете се с нас</h2>
                            
                            <div className="space-y-4 mb-8">
                                <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl hover:shadow-md transition-all">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <MapPin className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-800 mb-1">Адрес</h4>
                                        <p className="text-slate-600">
                                            гр. София, ул. Пиротска 85
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl hover:shadow-md transition-all">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <Phone className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-800 mb-1">Телефон</h4>
                                        <a href="tel:0899972727" className="text-slate-600 hover:text-amber-600 transition-colors text-lg font-medium">
                                            0899 97 2727
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl hover:shadow-md transition-all">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <Mail className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-800 mb-1">Имейл</h4>
                                        <a href="mailto:vkustnaturcia39@gmail.com" className="text-slate-600 hover:text-amber-600 transition-colors">
                                            vkustnaturcia39@gmail.com
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Working Hours Table */}
                            <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 rounded-2xl border border-slate-200">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow">
                                        <Clock className="h-5 w-5 text-white" />
                                    </div>
                                    <h4 className="font-bold text-slate-800 text-lg">Работно време</h4>
                                </div>
                                <div className="space-y-2">
                                    {workingHours.map((item, i) => (
                                        <div 
                                            key={i} 
                                            className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                                                i === dayIndex 
                                                    ? 'bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-300' 
                                                    : 'bg-white hover:bg-amber-50'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                {i === dayIndex && (
                                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                                )}
                                                <span className={`${i === dayIndex ? 'font-bold text-amber-800' : 'text-slate-600'}`}>
                                                    {item.day}
                                                </span>
                                            </div>
                                            <span className={`${i === dayIndex ? 'font-bold text-amber-800' : 'text-slate-800'}`}>
                                                {item.hours}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl border border-amber-100 shadow-xl">
                            <h3 className="font-serif text-2xl font-bold mb-6 text-slate-800">Изпратете съобщение</h3>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="name" className="text-slate-700">Име *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        className="mt-1 bg-white border-amber-200 focus:border-amber-400"
                                        data-testid="contact-name"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="email" className="text-slate-700">Имейл *</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        className="mt-1 bg-white border-amber-200 focus:border-amber-400"
                                        data-testid="contact-email"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="phone" className="text-slate-700">Телефон</Label>
                                    <Input
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="mt-1 bg-white border-amber-200 focus:border-amber-400"
                                        data-testid="contact-phone"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="message" className="text-slate-700">Съобщение *</Label>
                                    <Textarea
                                        id="message"
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        required
                                        rows={5}
                                        className="mt-1 bg-white border-amber-200 focus:border-amber-400"
                                        data-testid="contact-message"
                                    />
                                </div>

                                <Button 
                                    type="submit" 
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg rounded-xl shadow-lg"
                                    data-testid="contact-submit-btn"
                                >
                                    {loading ? 'Изпращане...' : (
                                        <>
                                            <Send className="h-5 w-5 mr-2" />
                                            Изпрати съобщение
                                        </>
                                    )}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Google Maps */}
            <section className="py-16 bg-gradient-to-b from-amber-50 to-white">
                <div className="container-main">
                    <div className="text-center mb-8">
                        <h2 className="font-serif text-3xl font-bold text-slate-800">Намерете ни</h2>
                        <p className="text-slate-500 mt-2">гр. София, ул. Пиротска 85</p>
                    </div>
                    <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200">
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2932.6648078097434!2d23.31667!3d42.7001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40aa856fa2e6c4c1%3A0x8a8a8a8a8a8a8a8a!2z0YPQuy4g0J_QuNGA0L7RgtGB0LrQsCA4NSwg0KHQvtGE0LjRjywg0JHRitC70LPQsNGA0LjRjw!5e0!3m2!1sbg!2sbg!4v1234567890"
                            width="100%"
                            height="450"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Вкусът на Турция - Локация"
                        />
                    </div>
                </div>
            </section>

            {/* Social Media CTA */}
            <section className="py-16 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white">
                <div className="container-main text-center">
                    <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Последвайте ни</h2>
                    <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
                        Бъдете първите, които научават за нови продукти и специални оферти
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        {socialLinks.map((social, i) => (
                            <a
                                key={i}
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 px-6 py-4 rounded-xl transition-all hover:scale-105"
                            >
                                <social.icon className="h-6 w-6" />
                                <span className="font-medium">{social.name}</span>
                            </a>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
